import { Radio } from "antd";
import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { CreateShipmentReviewDrawer } from "../../../../shared/shipmentDrawer/CreateShipmentReviewDrawer";
import { ButtonSpinner } from "../../../../../components/atoms/Button/Button.styles";
import { StyledFormRadioDiv } from "../../../../../components/atoms/Input/Input.styles";
import { PortalFooter } from "../../../../../components/molecules/Footer";
import { PortalHeader } from "../../../../../components/molecules/Header";
import { ModalOverlay, ModalT1 } from "../../../../../components/organisms/Modal";
import { StyledModalContent } from "../../../../../components/organisms/Modal/Modal.styles";
import { useNotification } from "../../../../../hooks/useNotification";
import {
  useCreateShipmentFromTransferAllocationMutation,
  useGeneratePicklistFromSalesOrderAllocationMutation,
  useReleaseMOAllocationMutation,
  useReleaseSalesOrderAllocationMutation,
  useUpdatePurchaseOrderAllocationStatusMutation,
  useUpdateTransferOrderAllocationStatusMutation,
} from "../../../../../store/apiSlice/orderApiSlice";
import {
  getAllocationApiErrorMessage,
  getCreateShipmentButtonTitle,
  getCreateShipmentClientError,
} from "./createShipmentHelpers";

export const AllocationAction = ({
  selectedOrderId,
  selectedOrderFromListview,
  setSelectedOrder,
  allocation,
}) => {
  const notify = useNotification();
  const [isOpenPicklistModal, setIsOpenPicklistModal] = useState(false);
  const [selectedPicklistType, setSelectedPicklistType] = useState("order");
  const [isCreateShipmentOpen, setIsCreateShipmentOpen] = useState(false);
  const [isAddToShipmentOpen, setIsAddToShipmentOpen] = useState(false);

  const [updateStatusForPurchaseOrderLine] = useUpdatePurchaseOrderAllocationStatusMutation();
  const [updateStatusForTransferOrderLine] = useUpdateTransferOrderAllocationStatusMutation();

  const [createShipmentFromTransferAllocation, { isLoading: isLoadingForTransfer }] =
    useCreateShipmentFromTransferAllocationMutation();
  const [generatePicklistFromSalesOrderAllocation, { isLoading: isLoadingPicklist }] =
    useGeneratePicklistFromSalesOrderAllocationMutation();
  const [releaseSalesOrderAllocation, { isLoading: isLoadingRelease }] =
    useReleaseSalesOrderAllocationMutation();
  const [releaseMOAllocation, { isLoading: isLoadingReleaseMO }] = useReleaseMOAllocationMutation();

  const isDisabledCreateShipment = useMemo(() => {
    return (
      selectedOrderFromListview.length < 1 ||
      selectedOrderFromListview.some(
        item => !["Cargo Ready", "Fully Allocated", "Partially Allocated"].includes(item.status),
      )
    );
  }, [selectedOrderFromListview]);

  const createShipmentButtonTitle = useMemo(
    () => getCreateShipmentButtonTitle(selectedOrderFromListview, allocation),
    [selectedOrderFromListview, allocation],
  );

  const cargoReadyBtnStatus = useMemo(
    () => selectedOrderFromListview?.find(order => order.status === "Cargo Ready"),
    [selectedOrderFromListview],
  );

  const handleCargoReadyStatus = async () => {
    try {
      let res = null;
      if (allocation === "purchase") {
        res = await updateStatusForPurchaseOrderLine({
          ids: selectedOrderId,
          status: "Cargo Ready",
        }).unwrap();
      } else {
        res = await updateStatusForTransferOrderLine({
          ids: selectedOrderId,
          status: "Cargo Ready",
        }).unwrap();
      }
      setSelectedOrder([]);
      notify({
        type: "success",
        message: res.detail,
      });
    } catch (error) {
      notify({
        type: "error",
        message: getAllocationApiErrorMessage(
          error,
          "Could not mark lines as Cargo Ready. Please try again.",
        ),
      });
    }
  };

  const openPicklistModal = () => setIsOpenPicklistModal(true);
  const closePicklistModal = () => setIsOpenPicklistModal(false);

  const handleGeneratePicklist = async () => {
    if (isLoadingPicklist || selectedOrderId.length === 0) return;

    try {
      const res = await generatePicklistFromSalesOrderAllocation({
        sale_ord_line_ids: selectedOrderId,
        type_id: selectedPicklistType,
      }).unwrap();

      const ids = res.picklist_ids || [];
      const message =
        ids.length > 1
          ? `Picklists ${ids.length} created successfully.`
          : "Picklist created successfully.";

      notify({ type: "success", message });
      setSelectedOrder([]);
      closePicklistModal();
    } catch (error) {
      notify({
        type: "error",
        message: getAllocationApiErrorMessage(
          error,
          "Could not generate picklist. Please try again.",
        ),
      });
    }
  };

  const openPurchaseShipmentDrawer = mode => {
    if (selectedOrderId.length === 0 || isDisabledCreateShipment) {
      return;
    }
    const clientError = getCreateShipmentClientError(selectedOrderFromListview, allocation);
    if (clientError) {
      notify({ type: "error", message: clientError });
      return;
    }
    if (mode === "add") {
      setIsAddToShipmentOpen(true);
      return;
    }
    setIsCreateShipmentOpen(true);
  };

  const confirmTransferShipment = async () => {
    if (
      isLoadingForTransfer ||
      selectedOrderId.length === 0 ||
      isDisabledCreateShipment
    ) {
      return;
    }

    const clientError = getCreateShipmentClientError(selectedOrderFromListview, allocation);
    if (clientError) {
      notify({ type: "error", message: clientError });
      return;
    }

    try {
      const res = await createShipmentFromTransferAllocation({ ids: selectedOrderId }).unwrap();

      if (!Array.isArray(res) || res.length === 0) {
        notify({
          type: "error",
          message:
            "No shipment was created from the selected lines. Check facility and " +
            "allocated quantity, then try again.",
        });
        return;
      }

      const message = (
        <>
          {res.length > 1 ? "Shipments " : "Shipment "}
          {res.map((item, index) => (
            <span key={item.id}>
              <Link
                className="text-white text-decoration-underline"
                to={`/shipment/po-to/${item.id}`}
              >
                {item.name}
              </Link>
              {index < res.length - 1 && ", "}
            </span>
          ))}{" "}
          {res.length > 1 ? "have" : "has"} been created!
          <Link to={`/shipment/po-to/${res[0]?.id}`} className="Toastify__detailForLink">
            {" "}
            View Details{" "}
          </Link>
        </>
      );
      notify({ type: "success", message });
      setSelectedOrder([]);
    } catch (error) {
      notify({
        type: "error",
        message: getAllocationApiErrorMessage(error),
      });
    }
  };

  const isEnabledGeneratePicklist = useMemo(() => {
    return (
      selectedOrderFromListview.length > 0 &&
      selectedOrderFromListview.every(item =>
        ["partially_allocated", "fully_allocated"].includes(item.status?.id),
      )
    );
  }, [selectedOrderFromListview]);

  const isEnabledRelease = useMemo(() => {
    return (
      selectedOrderFromListview.length === 1 &&
      ["partially_allocated", "fully_allocated"].includes(selectedOrderFromListview[0]?.status?.id)
    );
  }, [selectedOrderFromListview]);

  const isEnabledReleaseForMO = selectedOrderId?.length === 1;

  const handleReleaseSalesOrder = async () => {
    const id = selectedOrderFromListview[0]?.id;
    if (isLoadingRelease || !isEnabledRelease || !id) return;
    try {
      await releaseSalesOrderAllocation({ id }).unwrap();
      notify({
        type: "success",
        message: "Sales order allocation released successfully!",
      });
      setSelectedOrder([]);
    } catch (error) {
      notify({
        type: "error",
        message: getAllocationApiErrorMessage(
          error,
          "Failed to release allocation. Please try again.",
        ),
      });
    }
  };

  const handleReleaseMO = async () => {
    const id = selectedOrderFromListview[0]?.id;
    if (isLoadingReleaseMO || !isEnabledReleaseForMO || !id) return;
    try {
      await releaseMOAllocation({ id }).unwrap();
      notify({
        type: "success",
        message: "Manufacturing order allocation released successfully!",
      });
      setSelectedOrder([]);
    } catch (error) {
      notify({
        type: "error",
        message: getAllocationApiErrorMessage(
          error,
          "Failed to release allocation. Please try again.",
        ),
      });
    }
  };

  const isCreatingTransferShipment =
    allocation === "transfer" && isLoadingForTransfer;

  return (
    ["purchase", "transfer", "salesOrder", "manufacturingOrder"].includes(allocation) && (
      <>
        {allocation === "purchase" && isCreateShipmentOpen && (
          <CreateShipmentReviewDrawer
            isOpen={isCreateShipmentOpen}
            onClose={() => setIsCreateShipmentOpen(false)}
            lineIds={selectedOrderId}
            setSelectedOrder={setSelectedOrder}
          />
        )}
        {allocation === "purchase" && isAddToShipmentOpen && (
          <CreateShipmentReviewDrawer
            isOpen={isAddToShipmentOpen}
            mode="add"
            onClose={() => setIsAddToShipmentOpen(false)}
            lineIds={selectedOrderId}
            setSelectedOrder={setSelectedOrder}
          />
        )}
        {["purchase", "transfer"].includes(allocation) && (
          <>
            {allocation === "purchase" ? (
              <>
                <li>
                  <button
                    type="button"
                    title={createShipmentButtonTitle}
                    className={`tableButton ${
                      isDisabledCreateShipment ? "" : "activeState"
                    }`}
                    disabled={isDisabledCreateShipment}
                    onClick={() => openPurchaseShipmentDrawer("create")}
                  >
                    Create Shipment{" "}
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    title={createShipmentButtonTitle}
                    className={`tableButton ${
                      isDisabledCreateShipment ? "" : "activeState"
                    }`}
                    disabled={isDisabledCreateShipment}
                    onClick={() => openPurchaseShipmentDrawer("add")}
                  >
                    Add to shipment{" "}
                  </button>
                </li>
              </>
            ) : (
              <li>
                <button
                  type="button"
                  title={
                    isCreatingTransferShipment
                      ? "Grouping selected lines by facility and creating shipments."
                      : createShipmentButtonTitle
                  }
                  className={`tableButton ${
                    isDisabledCreateShipment || isCreatingTransferShipment ? "" : "activeState"
                  }`}
                  style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}
                  aria-busy={isCreatingTransferShipment}
                  disabled={isDisabledCreateShipment || isCreatingTransferShipment}
                  onClick={confirmTransferShipment}
                >
                  {isCreatingTransferShipment ? <ButtonSpinner /> : null}
                  {isCreatingTransferShipment ? "Creating shipment…" : "Create Shipment "}
                </button>
              </li>
            )}
            <li>
              <button
                onClick={handleCargoReadyStatus}
                className={`tableButton ${selectedOrderId.length > 0 && !cargoReadyBtnStatus ? "activeState" : ""}`}
                title={
                  cargoReadyBtnStatus
                    ? "To continue, unselect the row with 'Cargo Ready' status."
                    : selectedOrderId.length > 0
                      ? false
                      : "Action required! Choose a row or order line."
                }
                disabled={cargoReadyBtnStatus || selectedOrderId.length === 0}
              >
                Cargo Ready{" "}
              </button>
            </li>
          </>
        )}
        {allocation === "salesOrder" && (
          <>
            <li>
              <button
                title={
                  isEnabledGeneratePicklist ? false : "Action required! Choose a row or order line."
                }
                className={`tableButton ${isEnabledGeneratePicklist ? "activeState" : ""}`}
                onClick={openPicklistModal}
                disabled={!isEnabledGeneratePicklist}
              >
                Generate Picklist
              </button>
            </li>
            <li>
              <button
                title={isEnabledRelease ? false : "Action required! Choose a row or order line."}
                className={`tableButton ${isEnabledRelease ? "activeState" : ""}`}
                onClick={handleReleaseSalesOrder}
                disabled={!isEnabledRelease}
              >
                Release
              </button>
            </li>

            {isOpenPicklistModal && (
              <GeneratePicklistModal
                selectedPicklistType={selectedPicklistType}
                setSelectedPicklistType={setSelectedPicklistType}
                closePicklistModal={closePicklistModal}
                handleGeneratePicklist={handleGeneratePicklist}
                isLoading={isLoadingPicklist}
                selectedOrderId={selectedOrderId}
              />
            )}
          </>
        )}
        {allocation === "manufacturingOrder" && (
          <li>
            <button
              title={isEnabledReleaseForMO ? false : "Action required! Choose a row or order line."}
              className={`tableButton ${isEnabledReleaseForMO ? "activeState" : ""}`}
              onClick={handleReleaseMO}
              disabled={!isEnabledReleaseForMO}
            >
              Release
            </button>
          </li>
        )}
      </>
    )
  );
};

const modalOptions = [
  {
    id: "order",
    title: "One Per Order",
    description: "One Per Order creates one picklist for each sales order",
  },
  {
    id: "customer",
    title: "Group By Customer",
    description: "Combine Order By Customer into one picklist",
  },
  {
    id: "batch",
    title: "Batch Pick",
    description: "Combine All Selected Order into one picklist",
  },
];

const GeneratePicklistModal = ({
  selectedPicklistType,
  setSelectedPicklistType,
  closePicklistModal,
  handleGeneratePicklist,
  isLoading,
  selectedOrderId,
}) => {
  // Any hooks here first, before any conditional returns

  const rootModals = document.getElementById("root-modals");
  if (!rootModals) return null;

  return createPortal(
    <ModalOverlay aria-modal="true" role="dialog" aria-labelledby="picklist-title">
      <ModalT1 $minWidth={480} $maxWidth={600} onClick={e => e.stopPropagation()}>
        <PortalHeader title="Select Picklist Type" onClose={closePicklistModal} />

        <StyledModalContent>
          <PicklistRadioGroupWrapper>
            <Radio.Group
              id={"type_id"}
              name={"type_id"}
              value={selectedPicklistType}
              onChange={e => setSelectedPicklistType(e.target.value)}
            >
              {modalOptions.map(option => (
                <Radio key={option.id} value={option.id} className="radioOption">
                  <OptionContentContainer
                    className={selectedPicklistType === option.id ? "selected" : ""}
                  >
                    <OptionTitle>{option.title}</OptionTitle>
                    <OptionDescription>{option.description}</OptionDescription>
                  </OptionContentContainer>
                </Radio>
              ))}
            </Radio.Group>
          </PicklistRadioGroupWrapper>
        </StyledModalContent>

        <PortalFooter
          isDisabled={selectedOrderId.length === 0}
          onApply={handleGeneratePicklist}
          onCancel={closePicklistModal}
          applyTitle="Generate Picklist"
          isLoading={isLoading}
        />
      </ModalT1>
    </ModalOverlay>,
    rootModals,
  );
};

const OptionContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  color: #000;
  font-feature-settings:
    "liga" off,
    "clig" off;
  font-family: Inter;
  font-style: normal;
  line-height: 24px; /* 171.429% */
  padding: 8px;

  &.selected {
    color: #2060ea;
  }
`;

const OptionTitle = styled.div`
  font-size: 14px;
  font-weight: 400;
`;

const OptionDescription = styled.div`
  font-size: 12px;
  font-weight: 300;
`;

const PicklistRadioGroupWrapper = styled(StyledFormRadioDiv)`
  .radioOption {
    width: calc(100% + 32px);
    margin-left: -16px;
    margin-right: -16px;
    padding-left: 16px;
    padding-right: 16px;
    box-sizing: border-box;
    &:hover {
      background: #f7f8f9;
    }
  }
`;
